import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Row, Col, Card, Input, Select, Button, Space, Typography, Alert, message } from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    AppstoreOutlined,
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedCompany, setSelectedCompanyData } from '../../reducers/company/actions';
import useUsers from './hooks/useUsers';
import useDepartments from './hooks/useDepartments';
import useEmailGroups from './hooks/useEmailGroups';
import useEmailRecipientRules from './hooks/useEmailRecipientRules';
import useCompanyAccess from './useCompanyAccess';
import useCompanyAiFeatures from './useCompanyAiFeatures';
import AiFeaturesPanel from './AiFeaturesPanel';
import UserTable from './UserTable';
import DepartmentsPanel from './DepartmentsPanel';
import EmailGroupsPanel from './EmailGroupsPanel';
import EmailRecipientsPanel from './EmailRecipientsPanel';
import LicensesPanel from '../access-control/LicensesPanel';
import CalendarDrawer from './CalendarDrawer';
import CompanyModal from './modals/CompanyModal';
import { getCompanyDepartments, createDepartment } from './api';
import session from '../../utils/session';
import appConfig from '../../config';
import '../dashboard/Dashboard.css';
import './UserManagement.css';

const { Title } = Typography;

// ─── Count Badge ─────────────────────────────────────────────────────────────
const CountBadge = ({ count }) => (
    <span className="gh-users-count-badge">{count}</span>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const UserManagement = () => {
    const dispatch = useDispatch();
    const selectedCompany = useSelector((state) => state.company?.selectedCompany);
    const selectedCompanyData = useSelector((state) => state.company?.selectedCompanyData);
    const companyId = selectedCompany?.id || selectedCompany;

    const users = useUsers({ companyId });
    const departments = useDepartments({ companyId });
    const emailGroups = useEmailGroups({ companyId });

    // System-email recipient overrides — gohire-admin only
    const isGhAdmin = (session.get('userData')?.role || '') === appConfig.userRoles.ghAdmin;
    const emailRules = useEmailRecipientRules({ companyId: isGhAdmin ? companyId : null });

    // Access control (licenses + per-user bundles/features) — gohire-admin only
    const access = useCompanyAccess({ companyId: isGhAdmin ? companyId : null });

    // AI features (enabled / cap / this-month usage) — gohire-admin only (ADR-033)
    const ai = useCompanyAiFeatures({ companyId: isGhAdmin ? companyId : null });

    const [calendarUser, setCalendarUser] = useState(null);
    const [companyModalOpen, setCompanyModalOpen] = useState(false);

    // Refs for child imperative handles
    const userTableRef = useRef(null);
    const deptPanelRef = useRef(null);
    const emailPanelRef = useRef(null);

    // ── User search (debounced) ──────────────────────────────────────────
    const [userSearch, setUserSearch] = useState('');
    const userSearchDebounce = useRef(null);
    const handleUserSearch = useCallback((e) => {
        const val = e.target.value;
        setUserSearch(val);
        clearTimeout(userSearchDebounce.current);
        userSearchDebounce.current = setTimeout(() => users.setSearch(val), 300);
    }, [users.setSearch]);

    // ── Department filter options (non-paginated, for dropdown) ──────────
    const [deptFilterOptions, setDeptFilterOptions] = useState([]);
    const fetchDeptOptions = useCallback((cId) => {
        if (!cId) return;
        getCompanyDepartments(cId)
            .then((res) => {
                const depts = res.data || [];
                setDeptFilterOptions(depts.map((d) => ({ label: d.name, value: d.id })));
            })
            .catch(() => setDeptFilterOptions([]));
    }, []);
    useEffect(() => { fetchDeptOptions(companyId); }, [companyId, fetchDeptOptions]);

    // ── Department panel search (debounced) ──────────────────────────────
    const [deptSearch, setDeptSearch] = useState('');
    const deptSearchDebounce = useRef(null);
    const handleDeptSearch = useCallback((e) => {
        const val = e.target.value;
        setDeptSearch(val);
        clearTimeout(deptSearchDebounce.current);
        deptSearchDebounce.current = setTimeout(() => departments.setSearch(val), 300);
    }, [departments.setSearch]);

    // ── Email groups panel search (debounced) ────────────────────────────
    const [emailSearch, setEmailSearch] = useState('');
    const emailSearchDebounce = useRef(null);
    const handleEmailSearch = useCallback((e) => {
        const val = e.target.value;
        setEmailSearch(val);
        clearTimeout(emailSearchDebounce.current);
        emailSearchDebounce.current = setTimeout(() => emailGroups.setSearch(val), 300);
    }, [emailGroups.setSearch]);

    // Cleanup debounce timers
    useEffect(() => () => {
        clearTimeout(userSearchDebounce.current);
        clearTimeout(deptSearchDebounce.current);
        clearTimeout(emailSearchDebounce.current);
    }, []);

    // ── Company created handler ──────────────────────────────────────────
    const handleCompanyCreated = async (company) => {
        setCompanyModalOpen(false);
        if (!company?.id) return;

        // Create default HQ department before selecting the company,
        // so the hooks' initial fetch already sees it
        try {
            await createDepartment(company.id, {
                name: 'HQ',
                company_name: company.name,
                address: '',
                city: '',
                state: '',
                zipcode: '',
            });
        } catch (err) {
            message.error('Failed to create default HQ department');
        }

        // Refresh department dropdown options so UserModal picks up HQ
        fetchDeptOptions(company.id);

        // Now select the company — hooks will fire and pick up the HQ dept
        dispatch(setSelectedCompany({ selectedCompany: company.id }));
        dispatch(setSelectedCompanyData({ selectedCompanyData: company }));

        // Tell the topbar layout to refresh its company list
        window.dispatchEvent(new CustomEvent('gh:companies-changed'));
    };

    return (
        <div className="gh-users-page">
            {/* ── Top Bar — "Companies" + Add Company ──────────────── */}
            <div className="gh-users-filter-bar" style={{ justifyContent: 'space-between' }}>
                <Title level={4} style={{ margin: 0, whiteSpace: 'nowrap' }}>
                    Companies
                </Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCompanyModalOpen(true)}
                >
                    Add Company
                </Button>
            </div>

            {/* ── No Company Selected Overlay ────────────────────── */}
            {!companyId && (
                <div className="gh-users-no-company-overlay">
                    <AppstoreOutlined style={{ fontSize: 48, color: '#bfbfbf', marginBottom: 16 }} />
                    <Title level={4} style={{ margin: 0, color: '#8c8c8c' }}>
                        Please select a company to manage
                    </Title>
                    <p style={{ color: '#bfbfbf', marginTop: 8 }}>
                        Use the company selector in the top bar to get started
                    </p>
                </div>
            )}

            {companyId && (
                <>
                    {/* ── Company Name Row (when selected) ─────────────────── */}
                    {selectedCompanyData?.name && (
                        <div className="gh-company-name-row">
                            <Title level={5} style={{ margin: 0 }}>
                                {selectedCompanyData.name}
                            </Title>
                        </div>
                    )}

                    {/* ── Over-capacity seat banner (gohire-admin) ─────────── */}
                    {isGhAdmin && access.overCapacity && (
                        <div className="gh-dash-grid" style={{ paddingBottom: 0 }}>
                            <Alert
                                type="warning"
                                showIcon
                                message="One or more licenses are over their seat capacity. Access still works — review seat counts in the Licenses card below."
                            />
                        </div>
                    )}

                    {/* ── Users Table Card ─────────────────────────────────── */}
                    <div className="gh-dash-grid">
                        <Row gutter={[16, 16]}>
                            <Col xs={24}>
                                <Card
                                    className="gh-dash-card"
                                    title={
                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                            Users
                                            <CountBadge count={users.data.total} />
                                        </span>
                                    }
                                    extra={
                                        <Space>
                                            <Input.Search
                                                placeholder="Search by name or email..."
                                                value={userSearch}
                                                onChange={handleUserSearch}
                                                allowClear
                                                style={{ width: 260 }}
                                            />
                                            <Select
                                                placeholder="All Departments"
                                                allowClear
                                                style={{ minWidth: 180 }}
                                                options={deptFilterOptions}
                                                onChange={(val) => users.setDepartmentId(val)}
                                            />
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                onClick={() => userTableRef.current?.openAddModal()}
                                            >
                                                Add User
                                            </Button>
                                        </Space>
                                    }
                                >
                                    <UserTable
                                        ref={userTableRef}
                                        companyId={companyId}
                                        users={users}
                                        onOpenCalendar={(user) => setCalendarUser(user)}
                                        access={isGhAdmin ? access : null}
                                        isGhAdmin={isGhAdmin}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </div>

                    {/* ── Licenses (gohire-admin only) ─────────────────────── */}
                    {isGhAdmin && (
                        <div className="gh-dash-grid" style={{ paddingTop: 0 }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24}>
                                    <LicensesPanel
                                        licenses={access.licenses}
                                        catalog={access.catalog}
                                        usedFor={access.usedFor}
                                        onAddFromPlan={access.addFromPlan}
                                        onAddCustom={access.addCustom}
                                        onUpdate={access.update}
                                        onRemove={access.remove}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}

                    {/* ── AI features (gohire-admin only) — ADR-033 ─────────── */}
                    {isGhAdmin && (
                        <div className="gh-dash-grid" style={{ paddingTop: 0 }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24}>
                                    <AiFeaturesPanel companyId={companyId} ai={ai} />
                                </Col>
                            </Row>
                        </div>
                    )}

                    {/* ── Departments + Email Groups Cards ─────────────────── */}
                    <div className="gh-dash-grid" style={{ paddingTop: 0 }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={12}>
                                <Card
                                    className="gh-dash-card"
                                    title={
                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                            Departments
                                            <CountBadge count={departments.data.total} />
                                        </span>
                                    }
                                    extra={
                                        <Space>
                                            <Input
                                                placeholder="Search..."
                                                prefix={<SearchOutlined />}
                                                value={deptSearch}
                                                onChange={handleDeptSearch}
                                                allowClear
                                                style={{ width: 180 }}
                                            />
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                onClick={() => deptPanelRef.current?.openAddModal()}
                                            >
                                                Add
                                            </Button>
                                        </Space>
                                    }
                                >
                                    <DepartmentsPanel
                                        ref={deptPanelRef}
                                        companyId={companyId}
                                        departments={departments}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} lg={12}>
                                <Card
                                    className="gh-dash-card"
                                    title={
                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                            Email Groups
                                            <CountBadge count={emailGroups.data.total} />
                                        </span>
                                    }
                                    extra={
                                        <Space>
                                            <Input
                                                placeholder="Search..."
                                                prefix={<SearchOutlined />}
                                                value={emailSearch}
                                                onChange={handleEmailSearch}
                                                allowClear
                                                style={{ width: 180 }}
                                            />
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                onClick={() => emailPanelRef.current?.openAddModal()}
                                            >
                                                Add
                                            </Button>
                                        </Space>
                                    }
                                >
                                    <EmailGroupsPanel
                                        ref={emailPanelRef}
                                        companyId={companyId}
                                        emailGroups={emailGroups}
                                        companyUsers={users}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </div>

                    {/* ── System-Email Recipients (gohire-admin only) ──────── */}
                    {isGhAdmin && (
                        <div className="gh-dash-grid" style={{ paddingTop: 0 }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24}>
                                    <Card
                                        className="gh-dash-card"
                                        title="System Email Recipients"
                                        extra={(
                                            <span style={{ color: '#8c8c8c' }}>
                                                Control To / CC / BCC of automated emails for this company
                                            </span>
                                        )}
                                    >
                                        <EmailRecipientsPanel
                                            companyId={companyId}
                                            emailRules={emailRules}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </div>
                    )}
                </>
            )}

            <CalendarDrawer
                user={calendarUser}
                onClose={() => setCalendarUser(null)}
            />

            <CompanyModal
                open={companyModalOpen}
                onClose={() => setCompanyModalOpen(false)}
                onSuccess={handleCompanyCreated}
            />
        </div>
    );
};

export default UserManagement;
